import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Building2, Timer, History, Ticket } from 'lucide-react';

// Check if event is in the past
const isEventPast = (dateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(dateStr);
  eventDate.setHours(0, 0, 0, 0);
  return eventDate < today;
};

// Check if user is logged in as member
const isMemberLoggedIn = () => {
  return !!localStorage.getItem('memberToken');
};

// Countdown component for SMART sale
const SmartCountdown = ({ smartSaleEndDate, onExpired }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!smartSaleEndDate) return null;
      
      const saleCloseTime = new Date(smartSaleEndDate);
      const now = new Date();
      const difference = saleCloseTime.getTime() - now.getTime();

      if (difference <= 0) {
        setIsExpired(true);
        if (onExpired) onExpired();
        return null;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hoursLeft = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutesLeft = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const secondsLeft = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours: hoursLeft, minutes: minutesLeft, seconds: secondsLeft };
    };

    const initialTime = calculateTimeLeft();
    if (initialTime) setTimeLeft(initialTime);
    
    const timer = setInterval(() => {
      const newTime = calculateTimeLeft();
      if (newTime) setTimeLeft(newTime);
    }, 1000);

    return () => clearInterval(timer);
  }, [smartSaleEndDate, onExpired]);

  // If expired or no valid time, return null (don't render)
  if (isExpired || !timeLeft) return null;

  return (
    <div className="flex items-center justify-center gap-1">
      <div className="text-center">
        <span className="font-mono font-bold text-lg text-white">{String(timeLeft.days).padStart(2, '0')}</span>
        <span className="block text-[8px] text-white/50 uppercase">Días</span>
      </div>
      <span className="text-[var(--primary)] font-bold">:</span>
      <div className="text-center">
        <span className="font-mono font-bold text-lg text-white">{String(timeLeft.hours).padStart(2, '0')}</span>
        <span className="block text-[8px] text-white/50 uppercase">Hrs</span>
      </div>
      <span className="text-[var(--primary)] font-bold">:</span>
      <div className="text-center">
        <span className="font-mono font-bold text-lg text-white">{String(timeLeft.minutes).padStart(2, '0')}</span>
        <span className="block text-[8px] text-white/50 uppercase">Min</span>
      </div>
      <span className="text-[var(--primary)] font-bold">:</span>
      <div className="text-center">
        <span className="font-mono font-bold text-lg text-white">{String(timeLeft.seconds).padStart(2, '0')}</span>
        <span className="block text-[8px] text-white/50 uppercase">Seg</span>
      </div>
    </div>
  );
};

export const EventCard = ({ event, featured = false }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [smartSaleExpired, setSmartSaleExpired] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    setIsLoggedIn(isMemberLoggedIn());
    
    // Check if SMART sale is already expired on mount
    if (event.smart_sale_end_date) {
      const endDate = new Date(event.smart_sale_end_date);
      if (endDate <= new Date()) {
        setSmartSaleExpired(true);
      }
    }
  }, [event.smart_sale_end_date]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
    });
  };

  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const genreLabels = {
    opera: 'Ópera',
    ballet: 'Ballet',
    concierto: 'Concierto',
    teatro: 'Teatro',
    musical: 'Musical',
    cine: 'Cine'
  };

  // For "directo" events, check if date has passed
  // For "grabado" events, they're never considered "past" based on date
  const isLive = event.event_type === 'directo';
  const isPast = isLive && isEventPast(event.date);
  
  // Only show SMART sale box if:
  // - Event has SMART sale enabled
  // - Event is not past
  // - SMART sale has not expired
  // - Event has a valid smart_sale_end_date
  const showSmartSale = event.has_smart_sale && !isPast && !smartSaleExpired && event.smart_sale_end_date;

  const handleSmartClick = (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      navigate('/mi-cuenta');
    } else {
      navigate(`/evento/${event.id}`);
    }
  };

  return (
    <Link
      to={`/evento/${event.id}`}
      className={`event-card card-event block group ${featured ? 'md:col-span-2' : ''}`}
      data-testid={`event-card-${event.id}`}
    >
      {/* Image */}
      <div className={`relative overflow-hidden ${featured ? 'aspect-[21/9]' : 'aspect-[4/3]'}`}>
        <img
          src={event.image_url}
          alt={event.title}
          className={`event-card-image w-full h-full object-cover ${isPast ? 'grayscale-[30%]' : ''}`}
        />
        <div className="absolute inset-0 image-overlay" />
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex gap-2">
          {isPast ? (
            <span className="bg-white/20 text-white text-xs px-2 py-0.5 uppercase tracking-wider font-medium backdrop-blur-sm flex items-center gap-1">
              <History size={12} />
              Evento Pasado
            </span>
          ) : (
            <span className={event.event_type === 'directo' ? 'badge-live' : 'badge-recorded'}>
              {event.event_type === 'directo' ? 'En Directo' : 'Grabado'}
            </span>
          )}
          <span className="bg-[var(--secondary)] text-white text-xs px-2 py-0.5 uppercase tracking-wider font-medium">
            {genreLabels[event.genre] || event.genre}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className={`font-['Playfair_Display'] text-white group-hover:text-[var(--primary)] transition-colors duration-300 ${
          featured ? 'text-3xl' : 'text-xl'
        }`}>
          {event.title}
        </h3>

        <p className={`text-white/50 mt-3 line-clamp-2 ${featured ? 'text-base' : 'text-sm'}`}>
          {event.description}
        </p>

        {/* Date, Time, Venue Info */}
        <div className="mt-4 flex flex-wrap gap-3 text-sm text-white/50">
          <span className="flex items-center gap-1">
            <Calendar size={14} className="text-[var(--primary)]" />
            {isLive ? formatDate(event.date) : `Estreno ${formatFullDate(event.date)}`}
          </span>
          {isLive && (
            <span className="flex items-center gap-1">
              <Clock size={14} className="text-[var(--primary)]" />
              {event.time}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Building2 size={14} className="text-[var(--primary)]" />
            {event.venue}
          </span>
        </div>

        {/* SMART Sale Section - Only shows when SMART sale is active and not expired */}
        {showSmartSale && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/30 p-3">
              <div className="text-center mb-2">
                <span className="text-[10px] uppercase tracking-wider text-[var(--primary)] font-bold">
                  Venta SMART · 25% Dto
                </span>
              </div>
              <SmartCountdown 
                smartSaleEndDate={event.smart_sale_end_date} 
                onExpired={() => setSmartSaleExpired(true)} 
              />
              <p className="text-[8px] text-white/40 text-center mt-2">
                Tiempo restante para comprar con descuento
              </p>
              
              {/* Register/Buy button inside SMART box */}
              <button
                onClick={handleSmartClick}
                className="w-full mt-3 py-2 px-3 bg-[var(--primary)] text-black text-xs font-bold uppercase tracking-wider hover:bg-[var(--primary)]/80 transition-colors flex items-center justify-center gap-1"
              >
                <Ticket size={12} />
                {isLoggedIn ? 'Comprar SMART' : 'Regístrate Gratis'}
              </button>
            </div>
            
            {/* Ver Cines button below SMART box */}
            <span
              className="w-full mt-3 py-2 px-3 border border-white/20 text-white text-xs font-medium uppercase tracking-wider hover:bg-white/10 transition-colors text-center flex items-center justify-center gap-1"
            >
              <Building2 size={12} />
              Ver Cines
            </span>
          </div>
        )}

        {/* Ver Cines button for events WITHOUT SMART sale */}
        {!showSmartSale && !isPast && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <span
              className="w-full py-2 px-3 border border-white/20 text-white text-xs font-medium uppercase tracking-wider hover:bg-white/10 transition-colors text-center flex items-center justify-center gap-1"
            >
              <Building2 size={12} />
              Ver Cines
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default EventCard;
